/**
 * Tasks by status, four columns.
 *
 * A board is the right shape for the question this screen answers - "what is
 * moving and what is stuck" is a comparison between columns, not a scan down
 * one - so it stays a board. What changed is the surfaces: the columns are the
 * CRM's card and the cards are its rows, instead of four pastel tints that meant
 * nothing to anyone who had not read the colour map.
 *
 * The shape follows `projects/Roadmap.tsx` deliberately: two boards in the same
 * application that look like different products is the thing this redesign is
 * for.
 */
import { Link } from "react-router-dom";
import { CalendarClock, CheckSquare, EyeOff, FolderKanban, MessageSquare } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Task, TaskStatus } from "../../../lib/projects";
import { StatusPill, fmtDate } from "../../ui";
import { ScrollFade } from "../_shared/ScrollFade";
import { UserAvatars } from "../_shared/UserChips";
import { isOverdue } from "../projects/lib";
import { TASK_STATUSES, taskTone } from "../projects/status";
import { priorityIsNotable, priorityTone } from "./priority";
import { TimerMark } from "../../timer";
import { TaskRowMenu } from "./TaskRowMenu";

/** A card is wider than pill padding allows, so it takes the softened corners
 *  rather than the 999px ones - see `.crm-list-row-soft`. */
const CARD = "crm-prow crm-list-row-soft relative flex-col items-stretch gap-2.5 p-3.5";

/**
 * How many faces fit on a card, and how big.
 *
 * TWO, against four in the list row, because the room is not the same: a column
 * is 272px wide and the card's meta line already carries a priority pill, the
 * project's name, the date and the comment count, all of which wrap. Two discs
 * plus a "+N" is 52px of the 244px inside the card, and every face after that
 * pushes the date onto a line of its own. The list row is where you go to read
 * the full crew; the card only has to say whether this one is somebody's.
 *
 * 24px rather than the row's 28: the discs sit in a line of 11px chips, and a
 * face taller than the text beside it turns the card's second line into two
 * heights.
 */
const CARD_FACES = 2;
const CARD_FACE_SIZE = 24;

function TaskCard({
  task,
  onMove,
  onDelete,
}: {
  task: Task;
  onMove: (id: string, s: TaskStatus) => void;
  onDelete: (task: Task) => void;
}) {
  const t = useT();
  const late = isOverdue(task);
  const comments = task._count?.comments ?? 0;
  // See the same line in `TasksListRow`: undefined is "this row did not come
  // from the list endpoint", `{ done: 0, total: 0 }` is "there is no checklist".
  const checklist = task.checklistProgress;

  return (
    <div className={CARD}>
      <div className="flex items-start gap-2.5">
        <Link
          to={`/tasks/${task.id}`}
          className={cn(
            "crm-prow-n min-w-0 flex-1 after:absolute after:inset-0 after:rounded-[22px]",
            task.status === "done" && "crm-mut line-through",
          )}
        >
          {task.title}
        </Link>
        {/* The closed eye `TasksList` and `ProjectTasks` draw, on the card that
            shows the same task. `relative z-[1]` because the title link covers
            the whole card with an ::after, and a tooltip under that overlay
            never opens. */}
        {task.visibleToClient ? null : (
          <span
            className="crm-mut relative z-[1] mt-0.5 inline-flex shrink-0"
            role="img"
            title={t("projects.hiddenFromClient")}
            aria-label={t("projects.hiddenFromClient")}
          >
            <EyeOff className="size-3.5" />
          </span>
        )}

        {/* The same clock the two list views draw. The board is where two people
            most easily pick up the same card on the same morning, so the answer
            to "is anybody already on this" belongs here rather than only in the
            row menu, which has to be opened before it can say anything. Same
            `relative z-[1]` reason as the eye above it. */}
        <TimerMark taskId={task.id} />

        <TaskRowMenu
          task={task}
          small
          className="shrink-0"
          onMove={(s) => onMove(task.id, s)}
          onDelete={() => onDelete(task)}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Only a priority somebody chose gets a pill - see `priority.ts`. */}
        {priorityIsNotable(task.priority) ? (
          <StatusPill tone={priorityTone(task.priority)}>{t(`tasks.prio.${task.priority}`)}</StatusPill>
        ) : null}

        {task.project ? (
          <span className="crm-chip">
            <FolderKanban className="size-3" />
            {task.project.name}
          </span>
        ) : null}

        {task.dueDate ? (
          <span className={cn("crm-tnum flex items-center gap-1.5 text-[11px]", late ? "text-red-600" : "crm-mut")}>
            <CalendarClock className="size-3 shrink-0" />
            {fmtDate(task.dueDate)}
          </span>
        ) : null}

        {comments > 0 ? (
          <span className="crm-mut crm-tnum flex items-center gap-1 text-[11px]">
            <MessageSquare className="size-3" />
            {comments}
          </span>
        ) : null}

        {/* How much of the plan is ticked, and nothing about the task itself -
            a full checklist does not close a card, and the count is deliberately
            two quiet numbers rather than anything that reads as a state. Drawn
            only where there are boxes, the way everything else on this line
            disappears when it has nothing to say. */}
        {checklist && checklist.total > 0 ? (
          <span className="crm-mut crm-tnum flex items-center gap-1 text-[11px]" title={t("tasks.checklist")}>
            <CheckSquare className="size-3" />
            {checklist.done}/{checklist.total}
          </span>
        ) : null}

        {/* The labels. Uncapped, unlike the list row: a card wraps its meta line
            over as many rows as it needs, so a heavily tagged task makes its own
            card taller instead of pushing anything off the end. The tag filter
            in the toolbar applies to this view too, and a filter whose subject
            is invisible here would be one nobody can check. */}
        {task.tags.map((tag) => (
          <span key={tag} className="crm-chip">
            {tag}
          </span>
        ))}

        {/* Nobody on it draws NOTHING here, where the list row draws a dash.
            The two are not inconsistent, they are the same rule applied to two
            shapes: a grid cell has to hold its track open whatever it contains,
            while this line only ever shows what a card actually has - the
            project, the date and the comment count all disappear the same way.
            It matters today, because after the Perfex import no task has an
            assignee at all: a mark per card would put a meaningless glyph on
            every card of the board, and the point of the faces is that seeing
            one means something.

            Pushed to the end of the line, and `relative z-[1]` so the stack's
            tooltip clears the title link's card-wide ::after - without it the
            initials are unreadable and there is no way to ask who they are. */}
        {task.assignees.length ? (
          <span className="crm-push relative z-[1] inline-flex">
            <UserAvatars ids={task.assignees} max={CARD_FACES} size={CARD_FACE_SIZE} />
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function TaskBoard({
  tasks,
  onMove,
  onDelete,
}: {
  tasks: Task[];
  onMove: (id: string, s: TaskStatus) => void;
  onDelete: (task: Task) => void;
}) {
  const t = useT();

  return (
    <ScrollFade className="pb-4" deps={[tasks]}>
      <div className="flex min-w-max gap-3.5">
        {TASK_STATUSES.map((key) => {
          const items = tasks.filter((tk) => tk.status === key);
          return (
            <div key={key} className="crm-card flex w-[272px] shrink-0 flex-col gap-2.5 p-3.5">
              <div className="flex items-center gap-2 px-0.5 pb-0.5">
                <StatusPill tone={taskTone(key)}>{t(`tasks.status.${key}`)}</StatusPill>
                <span className="crm-mut crm-tnum crm-push text-[12px] font-bold">{items.length}</span>
              </div>

              {items.map((tk) => (
                <TaskCard key={tk.id} task={tk} onMove={onMove} onDelete={onDelete} />
              ))}

              {/* Deliberately not an `EmptyState`: four illustrated empties side
                  by side would shout louder than the work. */}
              {items.length === 0 ? (
                <p className="crm-mut px-1 py-5 text-center text-[12px]">{t("tasks.emptyCol")}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    </ScrollFade>
  );
}
